// src/components/PrintableImportSlip.js
import React, { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  GlobalStyles,
} from '@mui/material';

/* ---------- Helpers ---------- */
const VND = n =>
  new Intl.NumberFormat('vi-VN', { style: 'currency', currency: 'VND' }).format(n || 0);
const dmy = v =>
  v ? new Date(v?.toDate ? v.toDate() : v).toLocaleDateString('vi-VN') : 'N/A';

/* ---------- Auto fit text (shrink-to-fit) ---------- */
function AutoFitText({ children, min = 9, max = 14, step = 0.5, align = 'left', title }) {
  const containerRef = useRef(null);
  const spanRef = useRef(null);
  const [fontSize, setFontSize] = useState(max);

  const fit = useCallback(() => {
    const container = containerRef.current;
    const span = spanRef.current;
    if (!container || !span) return;

    let lo = min, hi = max, best = min;
    span.style.whiteSpace = 'nowrap';
    span.style.display = 'inline-block';

    while (hi - lo >= step) {
      const mid = Math.round(((lo + hi) / 2) * 10) / 10;
      span.style.fontSize = `${mid}px`;
      if (span.scrollWidth <= container.clientWidth - 2) {
        best = mid;
        lo = mid + step;
      } else {
        hi = mid - step;
      }
    }
    setFontSize(best);
  }, [min, max, step]);

  useLayoutEffect(() => {
    fit();
    const ro = new ResizeObserver(fit);
    if (containerRef.current) ro.observe(containerRef.current);
    return () => ro.disconnect();
  }, [fit]);

  useLayoutEffect(() => {
    fit();
  }, [children, fit]);

  return (
    <div
      ref={containerRef}
      style={{ width: '100%', overflow: 'hidden', textAlign: align, lineHeight: 1.2 }}
      title={title ?? (typeof children === 'string' ? children : undefined)}
    >
      <span
        ref={spanRef}
        style={{ fontSize, whiteSpace: 'nowrap', display: 'inline-block', verticalAlign: 'middle' }}
      >
        {children}
      </span>
    </div>
  );
}

/* ---------- Styles ---------- */
const border = '1px solid #d0d0d0';
const headCell = { border, fontWeight: 700, textAlign: 'center', whiteSpace: 'nowrap' };
const dataCell = { border, textAlign: 'center', whiteSpace: 'nowrap' };

const PrintableImportSlip = React.forwardRef(({ order, company = {} }, ref) => {
  if (!order) return null;

  const items = useMemo(() => order.items || [], [order]);

  return (
    <>
      <GlobalStyles
        styles={{
          '@page': { size: 'A4', margin: '10mm' },
          '@media print': {
            'html, body': { overflow: 'hidden !important' },
            '.print-fit': {
              overflow: 'hidden !important',
              width: '100% !important',
              maxWidth: '100% !important',
            },
            '.print-fit table': {
              tableLayout: 'fixed !important',
              width: '100% !important',
            },
            '.MuiTableHead-root .MuiTableCell-head': {
              backgroundColor: 'transparent !important',
              color: 'inherit !important',
            },
            '.print-fit::-webkit-scrollbar': { display: 'none' },
          },
        }}
      />

      <Box
        ref={ref}
        sx={{
          p: 3,
          background: '#fff',
          color: '#000',
          maxWidth: '190mm',
          m: '0 auto',
          fontFamily: 'Arial, sans-serif',
          fontSize: 14,
        }}
      >
        {/* ---------- Header ---------- */}
        <Grid container alignItems="center" sx={{ mb: 3 }}>
          <Grid item xs={8}>
            <Grid container spacing={1}>
              <Grid item>
                <img src={company.logo || '/logo.png'} alt="logo" style={{ width: 72, height: 72 }} />
              </Grid>
              <Grid item>
                <Typography sx={{ fontWeight: 700, fontSize: 18 }}>{company.name || ''}</Typography>
                <Typography variant="body2">Địa chỉ: {company.address || ''}</Typography>
                <Typography variant="body2">ĐT: {company.phone || ''} | MST: {company.taxCode || ''}</Typography>
              </Grid>
            </Grid>
          </Grid>

          <Grid item xs={4} sx={{ textAlign: 'center', mx: 'auto' }}>
            <Typography variant="h4" noWrap sx={{ fontWeight: 700, color: '#1976d2' }}>
              PHIẾU NHẬP KHO
            </Typography>
            <Typography>Số PO: <strong>{order.poNumber}</strong></Typography>
            <Typography>Ngày lập: {dmy(order.importDate)}</Typography>
          </Grid>
        </Grid>

        {/* ---------- Info box (không border, không nền) ---------- */}
        <Paper elevation={0} sx={{ p: 1.5, mb: 2, border: 'none', boxShadow: 'none', bgcolor: 'transparent' }}>
          <Grid container spacing={1} alignItems="center">
            <Grid item xs><Typography><strong>Nhà cung cấp:</strong> {order.supplierName}</Typography></Grid>
            <Grid item xs="auto"><Typography><strong>Mã NCC:</strong> {order.supplierCode}</Typography></Grid>
            <Grid item xs="auto"><Typography><strong>Kho nhập:</strong> {order.warehouse || 'N/A'}</Typography></Grid>
            <Grid item xs="auto"><Typography><strong>Số hóa đơn:</strong> {order.invoiceNumber || 'N/A'}</Typography></Grid>
            <Grid item xs><Typography><strong>Ghi chú:</strong> {order.note || 'Không có'}</Typography></Grid>
          </Grid>
        </Paper>

        {/* ---------- Table ---------- */}
        <TableContainer component={Paper} className="print-fit" variant="outlined" sx={{ overflowX: 'auto', width: '100%', maxWidth: '100%' }}>
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%' }}>
            <colgroup>
              <col style={{ width: '8%' }} />
              <col style={{ width: '34%' }} />
              <col style={{ width: '10%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '14%' }} />
              <col style={{ width: '20%' }} />
            </colgroup>

            <TableHead sx={{ '& .MuiTableCell-head': { backgroundColor: 'transparent', color: 'inherit' } }}>
              <TableRow>
                {['STT', 'Tên sản phẩm', 'Mã SP', 'SL', 'Đơn giá', 'Thành tiền'].map(h => (
                  <TableCell key={h} sx={headCell}>{h}</TableCell>
                ))}
              </TableRow>
            </TableHead>

            <TableBody>
              {items.map((it, i) => (
                <TableRow key={i}>
                  <TableCell sx={dataCell}><AutoFitText align="center">{i + 1}</AutoFitText></TableCell>
                  <TableCell sx={{ ...dataCell, textAlign: 'left' }}>
                    <AutoFitText align="left" max={14} min={9}>{it.productName}</AutoFitText>
                  </TableCell>
                  <TableCell sx={dataCell}><AutoFitText align="center" max={14} min={9}>{it.productSku}</AutoFitText></TableCell>
                  <TableCell sx={dataCell}><AutoFitText align="center">{it.quantity}</AutoFitText></TableCell>
                  <TableCell sx={{ ...dataCell, textAlign: 'right' }}><AutoFitText align="right">{VND(it.importPrice)}</AutoFitText></TableCell>
                  <TableCell sx={{ ...dataCell, textAlign: 'right' }}><AutoFitText align="right">{VND((it.quantity || 0) * (it.importPrice || 0))}</AutoFitText></TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* ---------- Totals ---------- */}
        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
          <Grid item xs={5}>
            <Paper variant="outlined" sx={{ p: 1, bgcolor: '#eaf2ff', display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Tổng cộng: </Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{VND(order.totalAmount)}</Typography>
            </Paper>
          </Grid>
        </Grid>

        {/* ---------- Signatures ---------- */}
        <Grid container sx={{ mt: 6 }} justifyContent="space-between" textAlign="center">
          {['Người lập phiếu', 'Thủ kho', 'Người giao hàng'].map(label => (
            <Grid item xs={4} key={label}>
              <Typography sx={{ fontWeight: 700 }}>{label}</Typography>
              <Typography sx={{ fontStyle: 'italic' }}>(Ký, ghi rõ họ tên)</Typography>
            </Grid>
          ))}
        </Grid>
      </Box>
    </>
  );
});

export default PrintableImportSlip;
